import { useCallback, useState } from 'react';
import { GoogleSignInMode, useAuth } from '../../contexts/AuthContext';

export type AuthMode = 'signin' | 'signup';

export interface LoginViewModel {
  busy: boolean;
  mode: AuthMode;
  setMode: (mode: AuthMode) => void;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    confirmPassword: string,
  ) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
}

export function useLoginViewModel(
  onSuccess: () => void,
  googleSignInMode: GoogleSignInMode = 'popup',
): LoginViewModel {
  const auth = useAuth();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<AuthMode>('signin');
  const [error, setError] = useState<string | null>(null);

  const wrap = useCallback(
    async (fn: () => Promise<void>, options?: { completesInPlace?: boolean }) => {
      setError(null);
      setBusy(true);
      try {
        await fn();
        if (options?.completesInPlace !== false) onSuccess();
      } catch (err: any) {
        setError(err?.message || 'Authentication failed');
      } finally {
        setBusy(false);
      }
    },
    [onSuccess],
  );

  return {
    busy,
    mode,
    setMode,
    error,
    signIn: (email, password) => wrap(() => auth.signIn(email, password)),
    signUp: (email, password, displayName, confirmPassword) =>
      wrap(async () => {
        if (password !== confirmPassword) throw new Error('Passwords do not match');
        await auth.signUp(email, password, displayName);
      }),
    signInWithGoogle: () =>
      wrap(() => auth.signInWithGoogle(googleSignInMode), {
        completesInPlace: googleSignInMode !== 'redirect',
      }),
  };
}
